"use client";

/**
 * Mobile marketing HERO — the narrow-viewport assembly. Eight sections, top →
 * bottom:
 *
 *   1. Nav            — logo · Preise · Anmelden           → HeroMobileNav
 *   2. Hero copy      — H1 + sub + button column.          → HeroMobileIntro
 *   3. Chip carousel  — 2-row sideways-scroll module chips. → HeroMobileChips
 *   4. Device window  — same product window as desktop, uniformly
 *                       scaled (~0.3×).                    → HeroMobileWindow
 *   5. Scroll stage   — "Chaos → ein System".              → HeroMobileStage
 *   6. Kundenstimmen  — customer video quotes.      → KundenstimmenSection
 *   7. Closing CTA    — dark panel.              → HeroMobileClosingCta
 *   8. Footer         — follow cards + footer bar.      → HeroMobileFooter
 *
 * Active-module + active-call state lives here and is threaded into the chip
 * carousel and the device window. None of the full-width desktop sections are
 * reused — every piece here is mobile-width specific.
 */
import type { ReactNode } from "react";
import { useState } from "react";

import "./hero.css";
import { HeroCompliance } from "./hero-compliance";
import { HeroMobileChips } from "./hero-mobile-chips";
import { HeroMobileClosingCta, HeroMobileIntro } from "./hero-mobile-cta";
import { HeroMobileFooter } from "./hero-mobile-footer";
import { HeroMobileNav } from "./hero-mobile-nav";
import { HeroMobileStage } from "./hero-mobile-stage";
import { HeroMobileWindow } from "./hero-mobile-window";
import { H } from "./hero-tokens";
import type { HeroModuleKey } from "./hero-types";
import { KundenstimmenSection } from "./sections/kundenstimmen-section";

export function HeroMobile(): ReactNode {
  const [active, setActive] = useState<HeroModuleKey>("inbox");
  const [inCall, setInCall] = useState(true);

  return (
    // Fluid: fills the real phone viewport (capped on tablets/landscape). NOT a
    // fixed 390px mockup — that overflowed narrow phones (the "kaputt" cause).
    <div
      className="mkhero"
      style={{
        width: "100%",
        maxWidth: 480,
        margin: "0 auto",
        overflowX: "clip",
        paddingBottom: 40,
        background: H.page,
        color: H.ink,
        fontFamily: H.sans,
      }}
    >
      <HeroMobileNav />
      <HeroMobileIntro />
      <HeroMobileChips active={active} setActive={setActive} />
      <HeroMobileWindow
        active={active}
        setActive={setActive}
        inCall={inCall}
        setInCall={setInCall}
      />
      <HeroCompliance compact />
      <HeroMobileStage />
      <KundenstimmenSection />
      <HeroMobileClosingCta />
      <HeroMobileFooter />
    </div>
  );
}
